import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { CACHE_TIME } from '@/config/queryDefaults';
import {
  Alert,
  Dialog,
  DialogContent,
  InputBase,
  Box,
  Typography,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { getValidationErrors } from '@/lib/validationErrors';
import { Search as SearchIcon, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/useDebounce';
import { searchService } from '@/services/search/SearchService';
import { useSearchMetadata, useSlashCommands } from '@/hooks/search';
import { SearchResults } from './SearchResults';
import { SlashCommandResults } from './SlashCommandResults';
import { recentCommandsStore, isDynamicGroup } from './commands';
import type { CommandContext, MatchedCommand } from './commands';
import type { SearchHit } from '@/types/core/Search';
import { useAuth } from '@/contexts/AuthContext';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';

/**
 * Keycap chips in the footer. These were inline `style` attributes with a fixed
 * `#e0e0e0`, which cannot see the theme at all — so the whole footer stayed light
 * in dark mode. As `sx` they follow the palette.
 */
const kbdSx = {
  fontFamily: 'inherit',
  px: '4px',
  py: '2px',
  borderRadius: '3px',
  bgcolor: (theme: Theme) =>
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.12) : '#e0e0e0',
} as const;

interface GlobalSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

export function GlobalSearchDialog({ open, onClose }: GlobalSearchDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { theme: currentTheme, language: currentLanguage, updatePreference } = useUserPreferences();

  const [query, setQuery] = useState('');
  /** Failure from a context action. The palette has no owning page to hand it to. */
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const debouncedQuery = useDebounce(query.trim(), 200);
  const inputRef = useRef<HTMLInputElement>(null);

  // Slash-command mode: local, synchronous, bypasses backend search entirely.
  const slash = useSlashCommands(query);
  const isSlashMode = slash.isSlashMode;

  // Fetch search metadata for dynamic shortcuts
  const { data: metadata } = useSearchMetadata();

  // Fetch recent searches (only in regular search mode)
  const { data: recentSearches } = useQuery({
    queryKey: ['search', 'recent'],
    queryFn: () => searchService.getRecentSearches(5),
    staleTime: CACHE_TIME.METADATA,
    enabled: open && !isSlashMode,
  });

  // Perform search when debounced query changes — skipped in slash mode.
  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['search', 'quick', debouncedQuery],
    queryFn: () => searchService.quickSearch({ query: debouncedQuery }),
    enabled: open && !isSlashMode && debouncedQuery.length >= 1,
  });

  // Calculate total number of results for keyboard navigation
  const totalResults = useMemo(() => {
    if (isSlashMode) return slash.flat.length;
    if (!searchResults?.groups) return 0;
    return searchResults.groups.reduce((acc, group) => acc + group.hits.length, 0);
  }, [isSlashMode, slash.flat.length, searchResults]);

  // Get flat list of hits for keyboard navigation
  const flatHits = useMemo(() => {
    if (!searchResults?.groups) return [];
    return searchResults.groups.flatMap(group => group.hits);
  }, [searchResults]);

  // Calculate group boundaries (first index of each group) for Tab navigation
  const groupBoundaries = useMemo(() => {
    if (isSlashMode) return slash.groupStartIndices;
    if (!searchResults?.groups) return [];
    const boundaries: number[] = [];
    let index = 0;
    for (const group of searchResults.groups) {
      boundaries.push(index);
      index += group.hits.length;
    }
    return boundaries;
  }, [isSlashMode, slash.groupStartIndices, searchResults]);

  // Build dynamic placeholder from metadata shortcuts
  const placeholder = useMemo(() => {
    if (isSlashMode) {
      return t('slashCommand.placeholder', 'Type a command...');
    }
    if (!metadata?.entities || metadata.entities.length === 0) {
      return t('search.placeholder', 'Search...');
    }
    // Get entities with shortcuts, sorted by weight (highest first)
    const withShortcuts = metadata.entities
      .filter(e => e.shortcutPrefix)
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 3);

    if (withShortcuts.length === 0) {
      const names = metadata.entities.slice(0, 3).map(e => e.displayName.toLowerCase());
      return `Search ${names.join(', ')}...`;
    }

    // Show shortcuts with their meaning: "o: orders, i: invoices, c: customers"
    const shortcuts = withShortcuts
      .map(e => `${e.shortcutPrefix}: ${e.displayName.toLowerCase()}`)
      .join(', ');
    return `Search... (${shortcuts}, / commands)`;
  }, [isSlashMode, metadata, t]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchResults, slash.flat]);

  // Reset selection when dialog opens, but keep the query (so last search persists)
  // User can clear query manually to see recent searches
  // Select existing text so user can type to replace or arrow to continue
  useEffect(() => {
    if (open) {
      setSelectedIndex(0);
      // Small delay to ensure input is mounted and focused
      setTimeout(() => {
        inputRef.current?.select();
      }, 0);
    }
  }, [open]);

  const handleSelect = useCallback((hit: SearchHit) => {
    // Record selection for history - store the selected result (e.g., "ORD-001075")
    // not the search query (e.g., "ORD-"), so clicking it later shows that specific item
    searchService.recordSelection({
      query: hit.primary,
      entityType: hit.entityType,
      entityId: hit.id,
    }).then(() => {
      // Invalidate recent searches cache so next open shows updated history
      queryClient.invalidateQueries({ queryKey: ['search', 'recent'] });
    });

    onClose();
    navigate(hit.url);
  }, [navigate, onClose, queryClient]);

  const runCommand = useCallback((matched: MatchedCommand) => {
    // Record MRU _before_ running so history is consistent even if run() throws.
    if (!isDynamicGroup(matched.command.group)) {
      recentCommandsStore.record(matched.command.id);
    }
    setActionError(null);
    const ctx: CommandContext = {
      navigate,
      closeDialog: onClose,
      logout,
      updatePreference,
      currentTheme,
      currentLanguage,
      t,
      reportError: (error: unknown) => {
        const errors = getValidationErrors(error);
        setActionError(
          errors.length > 0
            ? errors.map((e) => e.message).join(' · ')
            : t('common.unexpectedError', 'Something went wrong. Please try again.'),
        );
      },
    };
    void matched.command.run(ctx);
  }, [navigate, onClose, logout, updatePreference, currentTheme, currentLanguage, t]);

  const handleRecentSelect = useCallback((recentQuery: string) => {
    setQuery(recentQuery);
    // Reset selection to first result and focus input
    setSelectedIndex(0);
    // Use setTimeout to ensure React has updated the DOM
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, totalResults - 1));
        break;

      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;

      case 'Tab':
        // Tab: jump to next group, Shift-Tab: jump to previous group
        if (groupBoundaries.length > 0) {
          event.preventDefault();
          if (event.shiftKey) {
            // Find the previous group boundary
            const prevBoundary = groupBoundaries
              .filter(b => b < selectedIndex)
              .pop();
            setSelectedIndex(prevBoundary ?? 0);
          } else {
            // Find the next group boundary
            const nextBoundary = groupBoundaries.find(b => b > selectedIndex);
            setSelectedIndex(nextBoundary ?? groupBoundaries[groupBoundaries.length - 1] ?? 0);
          }
        }
        break;

      case 'Enter':
        event.preventDefault();
        if (isSlashMode) {
          const matched = slash.flat[selectedIndex];
          if (matched) runCommand(matched);
        } else if (event.metaKey || event.ctrlKey) {
          // Cmd-Enter or Ctrl-Enter: go to advanced search
          onClose();
          navigate(`/search?q=${encodeURIComponent(query)}`);
        } else if (flatHits[selectedIndex]) {
          handleSelect(flatHits[selectedIndex]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        onClose();
        break;
    }
  }, [totalResults, flatHits, selectedIndex, handleSelect, query, navigate, onClose, groupBoundaries, isSlashMode, slash.flat, runCommand]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          position: isMobile ? undefined : 'fixed',
          top: isMobile ? undefined : '8%',
          m: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100%' : '85vh',
          borderRadius: isMobile ? 0 : 2,
          width: isMobile ? '100%' : '750px',
        },
      }}
    >
      {/* Search input */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          gap: 1,
        }}
      >
        <SearchIcon sx={{ color: 'text.secondary' }} />
        <InputBase
          inputRef={inputRef}
          autoFocus
          fullWidth
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{
            fontSize: '1.1rem',
            '& input': {
              p: 0,
            },
          }}
        />
        {query && (
          <IconButton size="small" onClick={() => setQuery('')}>
            <Close fontSize="small" />
          </IconButton>
        )}
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      <Divider />

      {/* A context action failed. The palette stays open so the message survives. */}
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)} sx={{ borderRadius: 0 }}>
          {actionError}
        </Alert>
      )}

      {/* Results */}
      <DialogContent
        sx={{
          p: 0,
          height: isMobile ? 'calc(100vh - 64px)' : 480,
          overflow: 'auto',
        }}
      >
        {isSlashMode ? (
          <SlashCommandResults
            grouped={slash.grouped}
            flat={slash.flat}
            selectedIndex={selectedIndex}
            query={query.slice(1).trim()}
            onSelect={runCommand}
          />
        ) : (
          <SearchResults
            groups={searchResults?.groups || []}
            recentSearches={recentSearches}
            selectedIndex={selectedIndex}
            isLoading={isLoading && debouncedQuery.length >= 1}
            query={debouncedQuery}
            onSelect={handleSelect}
            onRecentSelect={handleRecentSelect}
            shortcuts={metadata?.shortcuts}
          />
        )}
      </DialogContent>

      {/* Footer with shortcuts */}
      <Divider />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04) : 'grey.50',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            <Box component="kbd" sx={kbdSx}>↑↓</Box>
            {' '}{t('search.navigate', 'Navigate')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <Box component="kbd" sx={kbdSx}>Tab</Box>
            {' '}{t('search.nextGroup', 'Next group')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <Box component="kbd" sx={kbdSx}>Enter</Box>
            {' '}{isSlashMode ? t('slashCommand.footer.run', 'Run command') : t('search.open', 'Open')}
          </Typography>
          {!isSlashMode && (
            <Typography variant="caption" color="text.secondary">
              <Box component="kbd" sx={kbdSx}>
                {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
              </Box>
              {' '}{t('search.advanced', 'Advanced')}
            </Typography>
          )}
        </Box>
        {!isSlashMode && searchResults && (
          <Typography variant="caption" color="text.secondary">
            {searchResults.totalCount < 0 ? `${searchResults.groups.reduce((acc, g) => acc + g.hits.length, 0)}+` : searchResults.totalCount} results in {searchResults.durationMs}ms
          </Typography>
        )}
        {isSlashMode && (
          <Typography variant="caption" color="text.secondary">
            {slash.flat.length} {t('slashCommand.commandsCount', 'commands')}
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}
